import {
  createContext,
  useContext,
  memo,
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  useThread,
  useThreadRuntime,
} from '@assistant-ui/react';
import ReactMarkdown from 'react-markdown';
import ReactECharts from 'echarts-for-react';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useCopilot } from './CopilotContext';
import { buildCopilotHeaders } from './copilotSession';
import { ReactComponent as AiIcon } from '../../assets/ai-stars.svg';
import { ReactComponent as CopyIcon } from './assets/copy.svg';
import { ReactComponent as ThumbUpIcon } from './assets/like.svg';
import { ReactComponent as ThumbUpFilledIcon } from './assets/like-filled.svg';
import { ReactComponent as ThumbDownIcon } from './assets/dislike.svg';
import { ReactComponent as ThumbDownFilledIcon } from './assets/dislike-filled.svg';
import { ReactComponent as NewChatIcon } from './assets/new-chat.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { ReactComponent as SubmitIcon } from './assets/submit.svg';
import { ReactComponent as StopIcon } from './assets/stop.svg';
import { ReactComponent as ReportIcon } from './assets/report.svg';
import { ReactComponent as ExportIcon } from './assets/export.svg';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { API } from '../../API';
import PublicationsPanel from './PublicationsPanel';
import ReportPanel from './ReportPanel';
import EmptyState from './EmptyState';
import {
  CopilotAnalyticsContext,
  useCopilotTrack,
  trackCopilotEvent,
  describeResponse,
  parseReferenceCount,
} from './analytics';
import {
  REPORT_UUID,
  parseReportRef,
  isSameReportRef,
  findLastReportRef,
  fetchCopilotReport,
  reportErrorMessage,
  saveBlob,
} from './copilotReportApi';
import styles from './CopilotButtonAssistantUI.module.scss';

// ---------------------------------------------------------------------------
// Context — passes the "open publications panel" callback into markdown renderers
// ---------------------------------------------------------------------------
const CopilotUIContext = createContext(null);

// ---------------------------------------------------------------------------
// ECharts block — renders ```echarts ... ``` code blocks as interactive charts
// ---------------------------------------------------------------------------
const EChartsBlock = ({ children }) => {
  const { t } = useTranslation();
  const track = useCopilotTrack();
  const chartRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  let option = null;
  let parseError = false;
  try {
    option = JSON.parse(String(children).trim());
  } catch {
    parseError = true;
  }

  const chartTitle = Array.isArray(option?.title)
    ? option?.title[0]?.text
    : option?.title?.text;

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (parseError) {
    return (
      <pre className={styles.codeBlock}>
        <code>{children}</code>
      </pre>
    );
  }

  const triggerDownload = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleExportJpeg = () => {
    const inst = chartRef.current?.getEchartsInstance();
    if (!inst) return;
    track('Export copilot chart', { format: 'jpeg' });
    triggerDownload(
      inst.getDataURL({ type: 'jpeg', pixelRatio: 2, backgroundColor: '#fff' }),
      'chart.jpg',
    );
    setDropdownOpen(false);
  };

  const handleExportJson = () => {
    track('Export copilot chart', { format: 'json' });
    const blob = new Blob([JSON.stringify(option, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, 'chart.json');
    URL.revokeObjectURL(url);
    setDropdownOpen(false);
  };

  const handleExportPdf = () => {
    const inst = chartRef.current?.getEchartsInstance();
    if (!inst) return;
    track('Export copilot chart', { format: 'pdf' });
    const dataUrl = inst.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    import('jspdf').then(({ jsPDF }) => {
      const pdf = new jsPDF({ orientation: 'landscape' });
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      pdf.addImage(dataUrl, 'PNG', 0, 0, w, h);
      pdf.save(`${chartTitle || 'chart'}.pdf`);
      setDropdownOpen(false);
    }).catch((err) => {
      console.error('PDF export failed:', err);
    });
  };

  return (
    <div className={styles.chartBlock}>
      <div className={styles.chartHeader}>
        {chartTitle && <span className={styles.chartTitle}>{chartTitle}</span>}
        <div className={styles.exportWrapper} ref={dropdownRef}>
          <button
            type="button"
            className={styles.exportIconBtn}
            onClick={() => setDropdownOpen((o) => !o)}
            aria-label={t('Export chart')}
            aria-expanded={dropdownOpen}
            aria-haspopup="menu"
            data-tooltip-id='export-chart-data'
            data-tooltip-content={t('Download')}
          >
            <ExportIcon aria-hidden="true" />
          </button>
          {dropdownOpen && (
            <div className={styles.exportDropdown} role="menu">
              <button type="button" role="menuitem" className={styles.exportDropdownItem} onClick={handleExportPdf}>
                {t('Download as PDF')}
              </button>
              <button type="button" role="menuitem" className={styles.exportDropdownItem} onClick={handleExportJson}>
                {t('Download as JSON')}
              </button>
              <button type="button" role="menuitem" className={styles.exportDropdownItem} onClick={handleExportJpeg}>
                {t('Download as JPEG')}
              </button>
            </div>
          )}
        </div>
      </div>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: 280, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge
      />
      <Tooltip id='export-chart-data' place='bottom' style={{ zIndex: 310, fontSize: '12px', padding: '4px 8px', lineHeight: '1.25rem' }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Markdown renderers
// ---------------------------------------------------------------------------

const PreRenderer = ({ children }) => {
  const codeEl = Array.isArray(children) ? children[0] : children;
  const className = codeEl?.props?.className || '';
  const lang = /language-(\w+)/.exec(className)?.[1];

  if (lang === 'echarts') {
    return <EChartsBlock>{codeEl?.props?.children}</EChartsBlock>;
  }

  return <pre className={styles.codeBlock}>{children}</pre>;
};

const CodeRenderer = ({ className, children }) => {
  // The agent sometimes puts the report URL inside a code span or fence, where
  // markdown never parses links — detect it and render the card anyway.
  const text = typeof children === 'string' ? children.trim() : '';
  if (/^https?:\/\/\S+$/.test(text) && REPORT_LINK_RE.test(text)) {
    return <ReportCard url={text} />;
  }
  return <code className={className}>{children}</code>;
};

// Report download links produced by the copilot `generate_report` tool:
// - short link: {COPILOT_API_URL}/dl/{narrative_id}/{report_uuid}
// - the agent sometimes pastes the resolved S3 presigned URL instead
//   (…/reports/{narrative_id}/{report_uuid}.pdf?X-Amz-…); accept it too so the
//   card still renders, even though that URL expires after a few minutes.
const REPORT_LINK_RE = new RegExp(
  `/(?:dl|reports)/\\d+/${REPORT_UUID}(?:\\.pdf)?(?:[?#]|$)`,
  'i',
);

const LinkRenderer = ({ href, children }) => {
  const ctx = useContext(CopilotUIContext);
  if (href === '#report-loading') {
    return <ReportCard url={null} />;
  }
  if (href?.startsWith('#ref-')) {
    return (
      <button
        type="button"
        className={styles.referenceBtn}
        onClick={() => ctx?.onReferenceClick?.(href, String(children))}
      >
        {children}
      </button>
    );
  }
  if (href && REPORT_LINK_RE.test(href)) {
    return <ReportCard url={href} />;
  }
  const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
  return (
    <a href={href} {...(isExternal && { target: '_blank', rel: 'noreferrer' })}>
      {children}
    </a>
  );
};

const MARKDOWN_COMPONENTS = { pre: PreRenderer, code: CodeRenderer, a: LinkRenderer };

// The agent may paste the report URL as bare text instead of a markdown link.
// ReactMarkdown (without remark-gfm) doesn't autolink bare URLs, so wrap them
// into markdown links; LinkRenderer then swaps them for the ReportCard.
// The trailing group grabs the presigned query string (required for the S3
// download to work) but must not eat sentence punctuation right after the URL.
const BARE_REPORT_URL_RE = new RegExp(
  `(?<!\\]\\()(?<!<)(?<!\\[)https?://[^\\s)]*/(?:dl|reports)/\\d+/${REPORT_UUID}(?:\\.pdf)?(?:[^\\s)]*[^\\s).,;:!?])?`,
  'gi',
);

// The agent sometimes wraps the report URL in backticks; inside a code span
// markdown never parses links, so drop the backticks before autolinking.
const CODE_WRAPPED_REPORT_URL_RE = new RegExp(
  `\`([^\`]*/(?:dl|reports)/\\d+/${REPORT_UUID}[^\`]*)\``,
  'gi',
);

const normalizeReportLinks = (text) =>
  text
    .replace(CODE_WRAPPED_REPORT_URL_RE, '$1')
    .replace(BARE_REPORT_URL_RE, (url) => `[${url}](${url})`);

// While the answer is still streaming, a report URL may be only partially
// received (presigned S3 URLs run to ~2KB of tokens) — without this, the
// half-arrived URL flows in as a growing wall of raw text and the card only
// appears once the stream ends. Replace the in-flight tail with a pending
// card (LinkRenderer maps #report-loading to a disabled ReportCard); it
// swallows an optional opening backtick / bracket / `[label](` prefix so no
// broken markdown is left behind.
const PARTIAL_REPORT_URL_RE =
  /(?:\[[^\]\n]*\]\()?[`[]{0,2}https?:\/\/[^\s)]*\/(?:dl|reports)\/[^\s)]*$/;

const maskPartialReportLink = (text) =>
  text.replace(PARTIAL_REPORT_URL_RE, '[…](#report-loading)');

// ---------------------------------------------------------------------------
// Message actions — copy, like, dislike
// ---------------------------------------------------------------------------
const MessageActions = ({ text, t, showCopy = true, traceId, onFeedback }) => {
  const track = useCopilotTrack();
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);

  const handleCopy = () => {
    track('Copy copilot response', { response_length: text.length });
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Rating is tracked in Amplitude on every "turn on" click regardless of
  // traceId — the backend feedback call below is skipped without one, so this
  // is the only record of the rating in that case.
  const handleLike = () => {
    const next = !liked;
    setLiked(next);
    if (disliked) setDisliked(false);
    if (next) track('Rate copilot response', { rating: 'positive', has_trace_id: Boolean(traceId) });
    if (next && traceId) onFeedback?.(traceId, 'like');
  };

  const handleDislike = () => {
    const next = !disliked;
    setDisliked(next);
    if (liked) setLiked(false);
    if (next) track('Rate copilot response', { rating: 'negative', has_trace_id: Boolean(traceId) });
    if (next && traceId) onFeedback?.(traceId, 'dislike');
  };

  return (
    <div className={styles.messageActions}>
      {showCopy && (
        <button
          type="button"
          className={`${styles.messageActionBtn} ${copied ? styles.messageActionBtnActive : ''}`}
          onClick={handleCopy}
          aria-label={copied ? t('Copied!') : t('Copy to clipboard')}
          data-tooltip-id="msg-actions-tooltip"
          data-tooltip-content={copied ? t('Copied!') : t('Copy to clipboard')}
        >
          <CopyIcon aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className={`${styles.messageActionBtn} ${liked ? styles.messageActionBtnActive : ''}`}
        onClick={handleLike}
        aria-label={t('Like')}
        aria-pressed={liked}
        data-tooltip-id="msg-actions-tooltip"
        data-tooltip-content={t('Like')}
      >
        {liked ? <ThumbUpFilledIcon aria-hidden="true" /> : <ThumbUpIcon aria-hidden="true" />}
      </button>
      <button
        type="button"
        className={`${styles.messageActionBtn} ${disliked ? styles.messageActionBtnActive : ''}`}
        onClick={handleDislike}
        aria-label={t('Dislike')}
        aria-pressed={disliked}
        data-tooltip-id="msg-actions-tooltip"
        data-tooltip-content={t('Dislike')}
      >
        {disliked ? <ThumbDownFilledIcon aria-hidden="true" /> : <ThumbDownIcon aria-hidden="true" />}
      </button>
      <Tooltip id="msg-actions-tooltip" place="bottom" style={{ zIndex: 310, fontSize: '12px', padding: '4px 8px', lineHeight: '1.25rem' }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Report card — rendered in place of a report download link inside a message.
// Uses <span> elements (not <div>) because markdown links render inside <p>.
// ---------------------------------------------------------------------------
const ReportCard = ({ url }) => {
  const { t } = useTranslation();
  const track = useCopilotTrack();
  const ctx = useContext(CopilotUIContext);
  const [downloading, setDownloading] = useState(false);
  // Both actions go through the dashboard-api proxy keyed by the
  // {narrative_id}/{uuid} in the link, so the card keeps working long after
  // the presigned URL the agent pasted has expired.
  const reportRef = useMemo(() => parseReportRef(url), [url]);
  // The card doubles as the toggle for the preview panel showing its report.
  const isPreviewOpen = isSameReportRef(ctx?.activeReport, reportRef) && !ctx?.reportClosing;

  const handlePreview = () => {
    if (!reportRef) return;
    if (!isPreviewOpen) track('Preview copilot report', { source: 'card' });
    ctx?.onReportToggle?.(reportRef);
  };

  const handleDownload = async () => {
    track('Download copilot report');
    if (!reportRef) {
      if (url) window.open(url, '_blank', 'noreferrer');
      return;
    }
    setDownloading(true);
    try {
      const { blob, fileName } = await fetchCopilotReport(reportRef);
      saveBlob(blob, fileName);
    } catch (error) {
      toast.error(reportErrorMessage(error, t));
    } finally {
      setDownloading(false);
    }
  };

  // The card body is the preview control and Download its own button, so
  // both are real buttons (no nested interactive elements) and keyboard users
  // get two tab stops with clear names.
  return (
    <span className={styles.reportCard}>
      <button
        type="button"
        className={styles.reportCardMain}
        onClick={handlePreview}
        disabled={!reportRef}
        aria-label={t('Preview report')}
        aria-expanded={isPreviewOpen}
      >
        <span className={styles.reportCardIconTile} aria-hidden="true">
          <ReportIcon className={styles.reportCardIcon} />
        </span>
        <span className={styles.reportCardText}>
          <span className={styles.reportCardTitle}>{t('Case report')}</span>
          <span className={styles.reportCardSubtitle}>{t('PDF document')}</span>
        </span>
      </button>
      <button
        type="button"
        className={styles.reportCardDownloadBtn}
        onClick={handleDownload}
        disabled={!url || downloading}
        aria-busy={downloading}
        aria-label={t('Download report')}
      >
        {t('Download')}
      </button>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Message rendering
// ---------------------------------------------------------------------------

const MessageItem = ({ message, t, isStreaming, traceId, onFeedback }) => {
  const text = message.content.find((p) => p.type === 'text')?.text ?? '';
  const status = message.status;
  const isError = status?.type === 'incomplete' && status?.reason === 'error';

  if (message.role === 'user') {
    return (
      <div className={styles.userMessage} role="article" aria-label="Your message">
        <p>{text}</p>
      </div>
    );
  }

  if (message.role === 'assistant') {
    if (isError) {
      return (
        <div className={styles.assistantMessage} role="article" aria-label="Copilot error">
          <p className={styles.errorInlineMessage} role="alert">
            {t("Couldn't get a response. Please try again.")}
          </p>
        </div>
      );
    }

    const displayText = normalizeReportLinks(isStreaming ? maskPartialReportLink(text) : text);

    return (
      <div className={styles.assistantMessage} role="article" aria-label="Copilot response">
        <ReactMarkdown components={MARKDOWN_COMPONENTS}>{displayText}</ReactMarkdown>
        {!isStreaming && text.trim() && <MessageActions text={text} t={t} traceId={traceId} onFeedback={onFeedback} />}
      </div>
    );
  }

  return null;
};

const LOADING_STEPS = [
  'Retrieving data',
  'Analyzing sources',
  'Cross-referencing',
  'Structuring response',
  'Generating response',
];

const TypingIndicator = ({ t }) => {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (stepIndex >= LOADING_STEPS.length - 1) return;
    const timer = setTimeout(() => setStepIndex((i) => i + 1), 5000);
    return () => clearTimeout(timer);
  }, [stepIndex]);

  const label = t(LOADING_STEPS[stepIndex]);

  return (
    <div className={styles.loadingState} role="status" aria-live="polite" aria-label={label}>
      <span className={styles.loader} aria-hidden="true" />
      <span key={stepIndex} className={styles.loadingText}>{label}</span>
    </div>
  );
};

// A request that was in flight when the tab reloaded. The backend cancels the
// turn on disconnect, so the request is shown outside the history followed by
// the same inline error Copilot uses for a failed answer, plus a retry (the
// request re-enters the history only when resent).
const InterruptedRequest = ({ t, interrupted, onSendAgain }) => (
  <>
    <div className={styles.userMessage} role="article" aria-label={t('Your message')}>
      <p>{interrupted.text}</p>
    </div>
    <div className={styles.assistantMessage} role="article" aria-label={t('Copilot error')}>
      <p className={styles.errorInlineMessage} role="alert">
        {t('Response generation was interrupted.')}{' '}
        <button type="button" className={styles.retryButton} onClick={onSendAgain}>
          {t('Retry')}
        </button>
      </p>
    </div>
  </>
);

// Reads messages from the thread context; shows EmptyState when empty
const MessageList = ({ t, onSelectTemplate, context, onShowPublications, traceIdMap, onFeedback, interrupted, onSendAgain, expired }) => {
  const messages = useThread((s) => s.messages);
  const isRunning = useThread((s) => s.isRunning);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  if (messages.length === 0 && !interrupted && !expired) {
    return <EmptyState t={t} onSelectTemplate={onSelectTemplate} context={context} onShowPublications={onShowPublications} />;
  }

  return (
    <div className={styles.messageList} role="log" aria-live="polite" aria-label={t('Chat history')}>
      {messages.map((msg, i) => (
        <MessageItem
          key={msg.id ?? i}
          message={msg}
          t={t}
          isStreaming={isRunning && i === messages.length - 1}
          traceId={traceIdMap?.get(msg.id ?? i)}
          onFeedback={onFeedback}
        />
      ))}
      {interrupted && !expired && <InterruptedRequest t={t} interrupted={interrupted} onSendAgain={onSendAgain} />}
      <div ref={endRef} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composer (input area)
// ---------------------------------------------------------------------------
const MAX_TEXTAREA_HEIGHT = 120; // ~5 lines before scroll

const ChatComposer = ({ t, onSubmit, isRunning, onStop, context, blocked = false }) => {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);

  const placeholder =
    context === 'dashboard' ? t('Ask about this dashboard...') : t('Ask about this case...');
  const inputDisabled = isRunning || blocked;

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
  }, []);

  const handleChange = (e) => {
    setText(e.target.value);
    adjustHeight();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || inputDisabled) return;
    onSubmit(trimmed);
    setText('');
    // Reset height after send
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.overflowY = 'hidden';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form className={styles.composer} onSubmit={handleSubmit} aria-label={t('Send a message')}>
      <label htmlFor="copilot-aui-input" className={styles.srOnly}>
        {placeholder}
      </label>
      <div className={`${styles.composerBox} ${blocked ? styles.composerBoxBlocked : ''}`}>
        <textarea
          ref={textareaRef}
          id="copilot-aui-input"
          className={styles.composerInput}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={inputDisabled}
          aria-disabled={inputDisabled}
        />
        {isRunning ? (
          <button
            type="button"
            className={styles.stopBtn}
            onClick={onStop}
            aria-label={t('Stop generation')}
          >
            <StopIcon aria-hidden="true" />
          </button>
        ) : (
          <button
            type="submit"
            className={`${styles.sendBtn} ${!text.trim() || blocked ? styles.sendBtnDisabled : ''}`}
            disabled={!text.trim() || blocked}
            aria-label={t('Send message')}
          >
            <SubmitIcon aria-hidden="true" />
          </button>
        )}
      </div>
      {blocked ? (
        <p className={styles.expiredHint} role="status">
          {t("This session has expired and can't be continued. Start a new chat.")}
        </p>
      ) : (
        <p className={styles.disclaimer}>{t('AI can make mistakes')}</p>
      )}
    </form>
  );
};

// ---------------------------------------------------------------------------
// Inner shell — must live inside AssistantRuntimeProvider to use thread hooks
// ---------------------------------------------------------------------------
const CopilotInner = ({
  t,
  onClose,
  onNewChat,
  onStop,
  canStartNewChat,
  context,
  conversation,
  traceIdMap,
  autoReport,
  clearAutoReport,
  reportModeRef,
  lastTurnModeRef,
  runStartedAtRef,
}) => {
  const threadRuntime = useThreadRuntime();
  const track = useCopilotTrack();
  const isRunning = useThread((s) => s.isRunning);
  const { page, expired } = useCopilot();
  // Side panel left of the chat: publications or a report preview, never both.
  const [activeRef, setActiveRef] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  // True while the report panel plays its slide-out; it unmounts via onClose.
  const [reportClosing, setReportClosing] = useState(false);
  const { narrativeId, narrativeTitle, narrativePath, interrupted } = conversation;

  const sendFeedback = useCallback(async (traceId, sentiment) => {
    const res = await API.Fetch('POST', '/API/v2/copilot/feedback', null, { trace_id: traceId, sentiment });
    if (!res || !res.ok) {
      toast.error(t("Couldn't get a response. Please try again."));
    }
  }, [t]);

  const handleFeedback = useCallback(
    (traceId, sentiment) => sendFeedback(traceId, sentiment),
    [sendFeedback],
  );

  const handleReferenceClick = useCallback((href, label, source = 'message_link') => {
    track('Open copilot references', { count: parseReferenceCount(href), source });
    setActiveReport(null);
    setReportClosing(false);
    setActiveRef({ href, label });
  }, [track]);

  const handleReportPreview = useCallback((reportRef) => {
    setActiveRef(null);
    setActiveReport(reportRef);
    setReportClosing(false);
  }, []);

  const handleReportClosed = useCallback(() => {
    setActiveReport(null);
    setReportClosing(false);
  }, []);

  // Card click: open its report, switch to it, or — when it is the one already
  // showing — close the panel. Reopening mid-animation just cancels the close.
  const handleReportToggle = useCallback(
    (reportRef) => {
      if (isSameReportRef(activeReport, reportRef) && !reportClosing) {
        setReportClosing(true);
      } else {
        handleReportPreview(reportRef);
      }
    },
    [activeReport, reportClosing, handleReportPreview],
  );

  // Open a freshly generated report (or its newest revision) as soon as the
  // panel is showing — immediately if open, otherwise on the next open.
  useEffect(() => {
    if (!autoReport) return;
    track('Preview copilot report', { source: 'auto' });
    handleReportPreview(autoReport);
    clearAutoReport();
  }, [autoReport, handleReportPreview, clearAutoReport, track]);

  const handleShowPublications = useCallback(() => {
    handleReferenceClick('#ref-publications?count=12', '12 Publications', 'suggestion');
  }, [handleReferenceClick]);

  const ctxValue = useMemo(
    () => ({
      onReferenceClick: handleReferenceClick,
      onReportToggle: handleReportToggle,
      activeReport,
      reportClosing,
    }),
    [handleReferenceClick, handleReportToggle, activeReport, reportClosing],
  );

  // turn_index = how many user turns the thread already holds (0 = first prompt)
  const userTurnCount = useCallback(
    () => threadRuntime.getState().messages.filter((m) => m.role === 'user').length,
    [threadRuntime],
  );

  // Every send records its mode so the provider can persist what to resend if
  // the tab reloads before the answer arrives. Prompt text never goes to
  // analytics; canned suggestion labels are safe to send.
  const sendMessage = useCallback(
    (text, mode, { source, suggestionLabel } = {}) => {
      track('Submit copilot prompt', {
        source,
        suggestion_label: suggestionLabel,
        mode,
        prompt_length: text.length,
        turn_index: userTurnCount(),
      });
      if (reportModeRef) reportModeRef.current = mode === 'report';
      if (lastTurnModeRef) lastTurnModeRef.current = mode;
      threadRuntime.append({ role: 'user', content: [{ type: 'text', text }] });
    },
    [threadRuntime, reportModeRef, lastTurnModeRef, track, userTurnCount],
  );

  const handleSend = useCallback((text) => sendMessage(text, 'qa', { source: 'typed' }), [sendMessage]);

  const handleSelectTemplate = useCallback(
    (query, { report = false } = {}) =>
      sendMessage(query, report ? 'report' : 'qa', {
        source: report ? 'report_template' : 'suggestion',
        suggestionLabel: query,
      }),
    [sendMessage],
  );

  const handleSendAgain = useCallback(() => {
    if (interrupted) sendMessage(interrupted.text, interrupted.mode, { source: 'retry' });
  }, [interrupted, sendMessage]);

  // Goes through the provider so a user-requested stop is told apart from the
  // browser aborting the stream on reload.
  const handleStop = useCallback(() => {
    track('Stop copilot generation', {
      elapsed_ms: runStartedAtRef?.current ? Date.now() - runStartedAtRef.current : null,
    });
    onStop();
  }, [onStop, track, runStartedAtRef]);

  const handleNewChat = useCallback(() => {
    track('Start new copilot chat', { messages_count: threadRuntime.getState().messages.length });
    onNewChat();
  }, [track, threadRuntime, onNewChat]);

  const handleClose = useCallback(() => {
    track('Close copilot', {
      messages_count: threadRuntime.getState().messages.length,
      closed_while_running: isRunning,
    });
    onClose();
  }, [track, threadRuntime, isRunning, onClose]);

  const newChatHint = canStartNewChat ? t('New chat') : t('New chat is unavailable on this page.');
  // The conversation follows the user around the app; once they have left
  // the case it belongs to, say which case that is and offer a way back.
  const showContextBar = page?.narrativeId !== narrativeId;

  return (
    <CopilotUIContext.Provider value={ctxValue}>
      <div className={`${styles.chatShell} ${activeRef || activeReport ? styles.chatShellWithPublications : ''}`}>
        {activeRef && (
          <PublicationsPanel
            href={activeRef.href}
            label={activeRef.label}
            narrativeId={narrativeId}
            onClose={() => setActiveRef(null)}
          />
        )}
        {activeReport && (
          <ReportPanel
            key={`${activeReport.narrativeId}/${activeReport.reportId}`}
            reportRef={activeReport}
            closing={reportClosing}
            onClose={handleReportClosed}
          />
        )}

        <div className={styles.modalContent}>
          {/* Header */}
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>{t('Osavul Copilot')}</span>
            <div className={styles.headerActions}>
              <button
                type="button"
                className={`${styles.newChatButton} ${!canStartNewChat ? styles.newChatButtonDisabled : ''}`}
                onClick={canStartNewChat ? handleNewChat : undefined}
                aria-disabled={!canStartNewChat}
                aria-label={newChatHint}
                data-tooltip-id="copilot-header-tooltip"
                data-tooltip-content={newChatHint}
              >
                <NewChatIcon aria-hidden="true" />
              </button>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label={t('Close copilot')}
                data-tooltip-id="copilot-header-tooltip"
                data-tooltip-content={t('Close')}
              >
                <CloseIcon aria-hidden="true" />
              </button>
            </div>
            <Tooltip id="copilot-header-tooltip" place="bottom" style={{ zIndex: 310, fontSize: '12px', padding: '4px 8px' }} />
          </div>

          {showContextBar && narrativeTitle && (
            <Link to={narrativePath || `/narratives/${narrativeId}`} className={styles.contextBar}>
              <span className={styles.contextBarTitle}>{narrativeTitle}</span>
              <span className={styles.contextBarHint}>{t('Your conversation with Copilot is based on this case.')}</span>
            </Link>
          )}

          {/* Chat area */}
          <div className={styles.chatArea}>
            <div className={styles.chatPanel}>
              <div className={styles.messagesScroll}>
                <MessageList
                  t={t}
                  onSelectTemplate={handleSelectTemplate}
                  context={context}
                  onShowPublications={handleShowPublications}
                  traceIdMap={traceIdMap}
                  onFeedback={handleFeedback}
                  interrupted={interrupted}
                  onSendAgain={handleSendAgain}
                  expired={expired}
                />
                {isRunning && <TypingIndicator t={t} />}
              </div>
              <div className={styles.inputArea}>
                <ChatComposer
                  t={t}
                  onSubmit={handleSend}
                  isRunning={isRunning}
                  onStop={handleStop}
                  context={context}
                  blocked={expired}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CopilotUIContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// ConversationBridge — stays mounted for the whole life of a conversation,
// open or closed, so a running answer keeps streaming while the panel is
// hidden or the user is on another page. Mirrors thread changes to the
// provider (persistence, unread) and keeps the per-answer trace ids.
// ---------------------------------------------------------------------------
const ConversationBridge = ({ pendingTraceIdRef, children }) => {
  const { registerThreadRuntime, reportActivity, reportTurnFinished } = useCopilot();
  const threadRuntime = useThreadRuntime();
  const messages = useThread((s) => s.messages);
  const isRunning = useThread((s) => s.isRunning);
  const prevIsRunningRef = useRef(false);
  const runStartedAtRef = useRef(null); // for the Stop event's elapsed_ms
  const [traceIdMap, setTraceIdMap] = useState(() => new Map());
  // A report the agent just produced, waiting to be shown in the preview
  // panel. Lives here (not in the panel UI) so a report that lands while the
  // panel is closed still opens as soon as the user comes back to the chat.
  const [autoReport, setAutoReport] = useState(null);
  const clearAutoReport = useCallback(() => setAutoReport(null), []);

  useEffect(() => {
    registerThreadRuntime(threadRuntime);
    return () => registerThreadRuntime(null);
  }, [registerThreadRuntime, threadRuntime]);

  useEffect(() => {
    if (!prevIsRunningRef.current && isRunning) runStartedAtRef.current = Date.now();
    const finished = prevIsRunningRef.current && !isRunning;
    const lastMsg = messages[messages.length - 1];
    let cancelled = false;
    if (finished) {
      if (lastMsg?.role === 'assistant' && pendingTraceIdRef?.current) {
        const key = lastMsg.id ?? messages.length - 1;
        const traceId = pendingTraceIdRef.current;
        setTraceIdMap((prev) => new Map(prev).set(key, traceId));
      }
      pendingTraceIdRef.current = null;
      cancelled = lastMsg?.status?.type === 'incomplete' && lastMsg?.status?.reason === 'cancelled';
      reportTurnFinished({ cancelled });
      if (!cancelled && lastMsg?.role === 'assistant') {
        // A revised report replaces the previous preview; the last link in
        // the answer is the newest version.
        const ref = findLastReportRef(lastMsg.content.find((p) => p.type === 'text')?.text);
        if (ref) setAutoReport(ref);
      }
    }
    prevIsRunningRef.current = isRunning;

    // Streaming deltas only change the last message's text, so the snapshot is
    // taken on message count and running-state changes rather than per chunk;
    // the final answer text is captured when the run ends.
    reportActivity({
      messages: messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content.find((p) => p.type === 'text')?.text ?? '' })),
      isRunning,
      cancelled,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isRunning]);

  return children({ traceIdMap, autoReport, clearAutoReport, runStartedAtRef });
};

// ThreadedCopilot owns the runtime; it is remounted (via the provider's
// threadKey) whenever a conversation starts, is discarded, or is restored.
const ThreadedCopilot = ({ adapter, initialMessages, pendingTraceIdRef, children }) => {
  const runtime = useLocalRuntime(adapter, { initialMessages: initialMessages?.length ? initialMessages : undefined });
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ConversationBridge pendingTraceIdRef={pendingTraceIdRef}>
        {children}
      </ConversationBridge>
    </AssistantRuntimeProvider>
  );
};


const MOCK_PUBLICATIONS_RESPONSE = `Based on the case analysis I identified **12 key publications** directly referencing this narrative.

The sources span investigative outlets, academic preprints, and NGO reports. Click below to review them:

[Reference: 12 publications](#ref-publications?count=12)

The most active publication window was **March–April 2025**, coinciding with the peak of coordinated amplification. A secondary cluster appeared in **Oct–Nov 2024** following the initial narrative seeding.`;

const MOCK_TREND_CHART_OPTION = {
  title: { text: 'Publication activity — last 12 months', left: 'center', textStyle: { fontSize: 13 } },
  tooltip: { trigger: 'axis' },
  xAxis: {
    type: 'category',
    data: ['Jun 24','Jul 24','Aug 24','Sep 24','Oct 24','Nov 24','Dec 24','Jan 25','Feb 25','Mar 25','Apr 25','May 25'],
    axisLabel: { rotate: 30, fontSize: 11 },
  },
  yAxis: { type: 'value', name: 'Publications' },
  series: [{
    name: 'Publications',
    type: 'line',
    data: [142, 167, 180, 155, 210, 198, 230, 245, 220, 290, 315, 340],
    smooth: true,
    areaStyle: { opacity: 0.15 },
    itemStyle: { color: '#2563eb' },
    lineStyle: { color: '#2563eb' },
  }],
};

const MOCK_TREND_RESPONSE = `Here is the publication trend for this case over the last 12 months:

\`\`\`echarts
${JSON.stringify(MOCK_TREND_CHART_OPTION)}
\`\`\`

Activity has grown steadily, with a **139% increase** from June 2024 to May 2025. The sharpest jump occurred in Q1 2025, likely driven by increased media coverage of the key narrative actors.

If you'd like a breakdown by actor or platform, just ask.`;

async function* mockStream(text) {
  const chunkSize = 40;
  for (let i = chunkSize; i <= text.length + chunkSize; i += chunkSize) {
    await new Promise((r) => setTimeout(r, 25));
    yield { content: [{ type: 'text', text: text.slice(0, i) }] };
  }
}

// ---------------------------------------------------------------------------
// Streaming adapter
// ---------------------------------------------------------------------------
// `conversationRef` points at the conversation the runtime belongs to, so the
// request always carries that conversation's case and session — not whatever
// page the user has navigated to meanwhile.
// Wraps an adapter so one place measures the whole turn: how long the answer
// took and whether it finished or failed. A user stop aborts the signal and
// produces no event here — handleStop already tracked it. reportModeRef is
// read before the inner run resets it so the event carries the right mode.
function withAnalytics(adapter, reportModeRef, trackRef) {
  return {
    async *run(opts) {
      const startedAt = Date.now();
      const mode = reportModeRef?.current === true ? 'report' : 'qa';
      let finalText = '';
      try {
        for await (const chunk of adapter.run(opts)) {
          finalText = chunk.content[0]?.text ?? finalText;
          yield chunk;
        }
        trackRef?.current?.('Receive copilot response', {
          mode,
          duration_ms: Date.now() - startedAt,
          ...describeResponse(finalText),
        });
      } catch (err) {
        if (!opts.abortSignal?.aborted) {
          // Only a bounded classification goes out: a stream error's message
          // is free-form backend text that may echo the prompt.
          const httpStatus = Number(/^Copilot API error: (\d+)$/.exec(err?.message ?? '')?.[1]) || null;
          trackRef?.current?.('Copilot response error', {
            mode,
            duration_ms: Date.now() - startedAt,
            error_type: httpStatus ? 'http' : err instanceof TypeError ? 'network' : 'stream',
            http_status: httpStatus,
          });
        }
        throw err;
      }
    },
  };
}

function buildAdapter(conversationRef, isSuperAdmin = false, onTraceIdRef = null, reportModeRef = null) {
  return {
    async *run({ messages, abortSignal }) {
      const { narrativeId, sessionId } = conversationRef.current ?? {};
      // const lastUserText = [...messages]
      //   .reverse()
      //   .find((m) => m.role === 'user')
      //   ?.content.find((c) => c.type === 'text')?.text ?? '';

      // if (lastUserText.toLowerCase().includes('trend chart')) {
      //   yield* mockStream(MOCK_TREND_RESPONSE);
      //   return;
      // }

      // if (lastUserText.toLowerCase().includes('publications')) {
      //   yield* mockStream(MOCK_PUBLICATIONS_RESPONSE);
      //   return;
      // }

      const headers = buildCopilotHeaders(sessionId);

      // A turn triggered by the Report-templates dropdown goes out with mode
      // 'report' — dashboard-api maps it to the "case-report-generator" model
      // alias and copilot-next injects its report instruction for that turn.
      // Everything else is a regular 'qa' message: the agent still calls
      // generate_report on its own when a typed message asks for a report,
      // and report turns stay in the history either way.
      const isReportTurn = reportModeRef?.current === true;
      if (reportModeRef) reportModeRef.current = false;

      // Report turns send no messages at all: the injected instruction is the
      // whole request. The backend takes the *last* user message in whatever
      // we send as the turn's text, so including the visible bubble text is
      // redundant and including prior history would wrongly scope the report
      // to the user's previous question.
      const apiMessages = isReportTurn
        ? []
        : messages
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .map((m) => ({
              role: m.role,
              text: m.content.find((c) => c.type === 'text')?.text ?? '',
            }));

      const response = await fetch('/API/v2/copilot/chat/stream', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          narrative_id: narrativeId,
          mode: isReportTurn ? 'report' : 'qa',
          messages: apiMessages,
        }),
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`Copilot API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let streamDone = false;
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') { streamDone = true; break; }
          let parsed;
          try {
            parsed = JSON.parse(raw);
          } catch (err) {
            if (raw) {
              const payload = isSuperAdmin ? raw : '[SSE_PAYLOAD_REDACTED]';
              console.error('SSE parse error, skipping chunk:', payload, err);
            }
            continue;
          }
          if (parsed.error) {
            throw new Error(parsed.error);
          }
          if (parsed.trace_id) {
            onTraceIdRef?.current?.(parsed.trace_id);
            continue;
          }
          accumulated +=
            parsed.choices?.[0]?.delta?.content ??
            parsed.content ??
            parsed.text ??
            parsed.delta ??
            '';
        }

        yield { content: [{ type: 'text', text: accumulated }] };
        if (streamDone) return;
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
// Mounted once, app-wide (inside CopilotProvider). Case pages register
// themselves with useCopilotPage; everything else comes from the provider.
export const CopilotButtonAssistantUI = memo(({ context = 'case' }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const copilot = useCopilot();
  const {
    visible,
    conversation,
    isOpen,
    unread,
    threadKey,
    canStartNewChat,
    open,
    close,
    startNewChat,
    cancelRun,
    lastTurnModeRef,
    getThreadSnapshot,
  } = copilot;

  const isSuperAdmin = currentUser?.is_super_admin === true;
  const pendingTraceIdRef = useRef(null);
  // Set by the Report-templates dropdown for exactly one turn: that request
  // goes out with mode 'report' (backend maps it to the report instruction).
  const reportModeRef = useRef(false);
  const onTraceIdRef = useRef((traceId) => {
    pendingTraceIdRef.current = traceId;
  });
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;

  // Amplitude tracker pre-bound to the current conversation. Case and session
  // are read at call time so events after "New chat" carry the new session.
  const track = useCallback(
    (event, props) =>
      trackCopilotEvent(
        event,
        {
          narrative_id: conversationRef.current?.narrativeId ?? null,
          context,
          session_id: conversationRef.current?.sessionId ?? null,
          ...props,
        },
        currentUser,
      ),
    [context, currentUser],
  );
  const trackRef = useRef(track);
  trackRef.current = track;

  const adapter = useMemo(
    () => withAnalytics(buildAdapter(conversationRef, isSuperAdmin, onTraceIdRef, reportModeRef), reportModeRef, trackRef),
    [isSuperAdmin],
  );

  // The trigger is unmounted while the dialog is open, so once it comes back
  // after a close, put keyboard focus back on it (dialog focus return).
  const triggerRef = useRef(null);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen && wasOpenRef.current) triggerRef.current?.focus();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    const { messagesCount, isRunning } = getThreadSnapshot();
    track('Open copilot', {
      has_history: messagesCount > 0,
      history_length: messagesCount,
      answer_in_progress: isRunning,
      has_unread_answer: unread,
    });
    open();
  }, [track, open, unread, getThreadSnapshot]);

  if (!visible) return null;

  const triggerLabel = unread ? t('Open Osavul Copilot, new answer') : t('Open Osavul Copilot');

  return (
    <CopilotAnalyticsContext.Provider value={track}>
      {!isOpen && (
        <button
          ref={triggerRef}
          type="button"
          className={styles.triggerButton}
          onClick={handleOpen}
          aria-label={triggerLabel}
          aria-haspopup="dialog"
        >
          <span className={styles.aiIcon} aria-hidden="true">
            <AiIcon />
          </span>
          {t('Osavul Copilot')}
          {unread && <span className={styles.unreadDot} aria-hidden="true" />}
        </button>
      )}

      {/* The runtime lives for the whole conversation; only the panel is
          shown and hidden, so an answer keeps streaming while it is closed. */}
      {conversation && (
        <ThreadedCopilot
          key={threadKey}
          adapter={adapter}
          initialMessages={conversation.initialMessages}
          pendingTraceIdRef={pendingTraceIdRef}
        >
          {({ traceIdMap, autoReport, clearAutoReport, runStartedAtRef }) =>
            isOpen ? (
              <div
                className={styles.modalOverlay}
                role="dialog"
                aria-modal="true"
                aria-label={t('Osavul Copilot')}
              >
                <CopilotInner
                  t={t}
                  onClose={close}
                  onNewChat={startNewChat}
                  onStop={cancelRun}
                  canStartNewChat={canStartNewChat}
                  context={context}
                  conversation={conversation}
                  traceIdMap={traceIdMap}
                  autoReport={autoReport}
                  clearAutoReport={clearAutoReport}
                  reportModeRef={reportModeRef}
                  lastTurnModeRef={lastTurnModeRef}
                  runStartedAtRef={runStartedAtRef}
                />
              </div>
            ) : null
          }
        </ThreadedCopilot>
      )}
    </CopilotAnalyticsContext.Provider>
  );
});
